import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { AccClaimService } from '@principle-theorem/ng-funding-requests';
import {
  EmptyStateComponent,
  ListSkeletonComponent,
} from '@principle-theorem/ng-principle-shared';
import { PipesModule } from '@principle-theorem/ng-shared';
import { PatientForm } from '@principle-theorem/principle-core';
import {
  isAccPatientForm,
  type IPatient,
  type IPatientForm,
} from '@principle-theorem/principle-core/interfaces';
import {
  HISTORY_DATE_TIME_FORMAT,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { MomentModule } from 'ngx-moment';
import { ReplaySubject } from 'rxjs';
import { FormStatusComponent } from '../form-status/form-status.component';
import { PatientSubmittedFormsListStore } from './patient-submitted-forms-list.store';

@Component({
  selector: 'pr-patient-submitted-forms-list',
  templateUrl: './patient-submitted-forms-list.component.html',
  styleUrls: ['./patient-submitted-forms-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PatientSubmittedFormsListStore, AccClaimService],
  imports: [
    CommonModule,
    EmptyStateComponent,
    FormStatusComponent,
    ListSkeletonComponent,
    PipesModule,
    MomentModule,
  ],
})
export class PatientSubmittedFormsListComponent {
  readonly store = inject(PatientSubmittedFormsListStore);
  readonly accClaimService = inject(AccClaimService);
  readonly dateTimeFormat = HISTORY_DATE_TIME_FORMAT;
  patient$ = new ReplaySubject<WithRef<IPatient>>(1);

  @Output() formSelected = new EventEmitter<WithRef<IPatientForm>>();

  @Input() imageEmptyState: boolean = false;
  @Input() loadingState: boolean = true;

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  @Input({ transform: coerceBooleanProperty })
  set hideConfirmedForms(hideConfirmedForms: boolean) {
    this.store.hideConfirmedForms(hideConfirmedForms);
  }

  constructor() {
    this.store.loadSubmittedForms(this.patient$);
  }

  async openForm(form: WithRef<IPatientForm>): Promise<void> {
    if (isAccPatientForm(form)) {
      const patient = await snapshot(this.patient$);
      return this.accClaimService.openPatientForm(form, patient);
    }

    this.formSelected.emit(form);
  }

  getFormName(form: WithRef<IPatientForm>): string {
    return PatientForm.getName(form);
  }
}
